import type { DayMark } from '@/components/Calendar';
import type { SavedFlight } from '@shared/types';
import { chipHasKidsLabel, isVisitationFree } from '@/lib/custodySchedule';
import { federalHoliday } from '@/lib/federalHolidays';
import { schoolBreakLabel, schoolBreakLanes, schoolBreaksOn } from '@/lib/schoolBreaks';
import { stayBandRaise, stayLanes, staySegmentOn, staysOn } from '@/lib/trips';

/** EWR↔RSW only. Other saved rows (for example ACA3210 / Unknown) stay on My Flight. */
export function isEwrRswFlight(flight: Pick<SavedFlight, 'origin' | 'destination'>): boolean {
  return (
    (flight.origin === 'EWR' && flight.destination === 'RSW') ||
    (flight.origin === 'RSW' && flight.destination === 'EWR')
  );
}

/** Saved EWR↔RSW flights on one calendar day, earliest departure first. */
export function savedFlightsOnDate(flights: readonly SavedFlight[], dateKey: string): SavedFlight[] {
  return flights
    .filter((f) => f.flight_date === dateKey && isEwrRswFlight(f))
    .slice()
    .sort(
      (a, b) =>
        (a.scheduled_departure ?? '').localeCompare(b.scheduled_departure ?? '') ||
        a.ident.localeCompare(b.ident),
    );
}

/** On-cell flight mark. One flight reads "FLT"; several show the count. */
export function flightMarkText(count: number): string | undefined {
  if (count < 1) return undefined;
  return count === 1 ? 'FLT' : String(count);
}

function flightLabel(count: number): string | null {
  if (count < 1) return null;
  return count === 1 ? 'saved flight' : `${count} saved flights`;
}

/**
 * Calendar day decoration: visitation-free / Chip Friday / federal holiday,
 * plus a saved-flight mark that does not replace those.
 *
 * Priority for the background tint matches the previous Flights page rule:
 * Chip Friday beats holiday beats the plain free/green tint. A holiday on
 * a Chip Friday still shows both captions under the Chip background.
 * The flight mark is a separate field so it stays visible on top of any
 * of those.
 *
 * A school break adds `has-school-break` beside whichever tint won, plus
 * one lane per school (`breakLanes`, Barnstable then Skidmore). A condo
 * stay or household trip adds its own lane after the school lanes, in
 * the color of whoever is there that day. The lanes do not replace the
 * tint, the caption, or the flight mark. Break names and the names of
 * who is at the condo are appended to the accessible label only.
 */
export function markCalendarDay(dateKey: string, savedCount: number): DayMark | undefined {
  const holiday = federalHoliday(dateKey);
  const chip = chipHasKidsLabel(dateKey);
  const free = isVisitationFree(dateKey);
  const flight = flightMarkText(savedCount);
  const breaks = schoolBreaksOn(dateKey);
  const stays = staysOn(dateKey);
  const schoolLanes = schoolBreakLanes(dateKey);
  const raise = stayBandRaise(
    schoolLanes.some((lane) => lane.id === 'barnstable'),
    schoolLanes.some((lane) => lane.id === 'skidmore'),
  );
  const breakLanes = [
    ...schoolLanes,
    ...stayLanes(dateKey).map((lane, i) => {
      const slot = raise + i;
      return slot > 0 ? { ...lane, raise: slot } : lane;
    }),
  ];

  const tint = chip ? 'is-chip-friday' : holiday ? 'is-holiday' : free ? 'is-visitation-free' : undefined;
  const className = [tint, breakLanes.length > 0 ? 'has-school-break' : undefined].filter(Boolean).join(' ') || undefined;
  const captions = [chip ? 'Chip' : null, holiday?.abbr ?? null].filter((c): c is string => Boolean(c));
  const labels = [
    chip,
    holiday?.name ?? null,
    ...breaks.map(schoolBreakLabel),
    ...stays.map((s) => staySegmentOn(s, dateKey)?.label ?? s.name),
    flightLabel(savedCount),
  ].filter((l): l is string => Boolean(l));

  if (!className && captions.length === 0 && !flight) return undefined;

  return {
    className,
    caption: captions.length > 0 ? captions.join(' · ') : undefined,
    flightMark: flight,
    label: labels.length > 0 ? labels.join(' — ') : undefined,
    breakLanes: breakLanes.length > 0 ? breakLanes : undefined,
  };
}
